import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  IonPage,
  IonContent,
  IonButton,
  IonIcon,
  IonItem,
  IonInput,
  IonTextarea,
  IonNote,
  IonSpinner,
} from '@ionic/react';
import {
  arrowBack,
  arrowForward,
  checkmark,
  checkmarkCircle,
  mail,
  lockClosed,
  personOutline,
  location,
  create,
  sparkles,
  people,
  shieldCheckmark,
  time,
} from 'ionicons/icons';
import { LogoLink } from '@/components/LogoLink';
import { NoindexSeo } from '@/components/NoindexSeo';
import { AppFooter } from '@/components/AppFooter';

interface AgentForm {
  email: string;
  password: string;
  displayName: string;
  bio: string;
  contactPhone: string;
  whatsapp: string;
  areaLabel: string;
}

const initialForm: AgentForm = {
  email: '',
  password: '',
  displayName: '',
  bio: '',
  contactPhone: '',
  whatsapp: '',
  areaLabel: '',
};

const STEPS = [
  { key: 'account', label: 'Account', icon: mail },
  { key: 'profile', label: 'Profile', icon: personOutline },
  { key: 'area', label: 'Area', icon: location },
  { key: 'review', label: 'Review', icon: checkmarkCircle },
];

function ReviewRow({ label, value, onEdit }: { label: string; value: string; onEdit: () => void }) {
  return (
    <div className="bp-reg-review-row">
      <div className="bp-reg-review-text">
        <div className="bp-reg-review-label">{label}</div>
        <div className="bp-reg-review-value">{value || '—'}</div>
      </div>
      <IonButton fill="clear" className="bp-reg-review-edit" onClick={onEdit}>
        <IonIcon icon={create} />
      </IonButton>
    </div>
  );
}

export function AgentRegistrationWizard() {
  const history = useHistory();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<AgentForm>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [complete, setComplete] = useState(false);
  const [submissionError, setSubmissionError] = useState('');

  const set = (field: keyof AgentForm, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: '' }));
  };

  const goTo = (s: number) => setStep(s);

  const validateStep = (): boolean => {
    const errs: Record<string, string> = {};
    if (step === 0) {
      if (!form.email) errs.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Invalid email';
      if (!form.password) errs.password = 'Password is required';
      else if (form.password.length < 8) errs.password = 'Must be at least 8 characters';
    } else if (step === 1) {
      if (!form.displayName) errs.displayName = 'Display name is required';
    } else if (step === 2) {
      if (!form.areaLabel) errs.areaLabel = 'Area is required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const next = () => {
    if (validateStep()) setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setSubmitting(true);
    setSubmissionError('');
    try {
      const res = await fetch('/api/v1/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email,
          password: form.password,
          firstName: form.displayName.split(' ')[0] || form.displayName,
          lastName: form.displayName.split(' ').slice(1).join(' ') || form.displayName,
          role: 'candidate',
          agentProfile: {
            displayName: form.displayName,
            bio: form.bio || undefined,
            contactPhone: form.contactPhone || undefined,
            whatsapp: form.whatsapp || undefined,
            area: [{ label: form.areaLabel, plusCodeShort: '', h3Index: '', h3Resolution: 8 }],
            h3Resolution: 8,
          },
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setComplete(true);
      } else {
        setSubmissionError(data.detail || data.message || 'Registration failed. Please try again.');
      }
    } catch {
      setSubmissionError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const highlights = [
    { icon: time, title: 'Under 5 minutes', desc: 'Quick, guided setup — no complicated forms' },
    { icon: people, title: 'Grow your network', desc: 'Onboard candidates and employers in your area' },
    { icon: shieldCheckmark, title: 'Trusted platform', desc: 'Verified users, POPIA-compliant marketplace' },
  ];

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="bp-reg-step-body">
            <div className="bp-reg-step-head">
              <h2 className="bp-reg-step-title">Create your account</h2>
              <p className="bp-reg-step-sub">Use a real email — we'll send a verification link.</p>
            </div>
            <div className="bp-reg-fields">
              <div className="bp-reg-field">
                <label className="bp-reg-label">Email</label>
                <IonItem className="bp-reg-input" lines="none">
                  <IonIcon icon={mail} slot="start" className="bp-reg-input-icon" />
                  <IonInput
                    type="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onIonInput={(e) => set('email', e.detail.value ?? '')}
                  />
                </IonItem>
                {errors.email && <IonNote className="bp-reg-error">{errors.email}</IonNote>}
              </div>
              <div className="bp-reg-field">
                <label className="bp-reg-label">Password</label>
                <IonItem className="bp-reg-input" lines="none">
                  <IonIcon icon={lockClosed} slot="start" className="bp-reg-input-icon" />
                  <IonInput
                    type="password"
                    placeholder="At least 8 characters"
                    value={form.password}
                    onIonInput={(e) => set('password', e.detail.value ?? '')}
                  />
                </IonItem>
                {errors.password && <IonNote className="bp-reg-error">{errors.password}</IonNote>}
              </div>
            </div>
          </div>
        );
      case 1:
        return (
          <div className="bp-reg-step-body">
            <div className="bp-reg-step-head">
              <h2 className="bp-reg-step-title">Your profile</h2>
              <p className="bp-reg-step-sub">Tell us about yourself. You can update this later.</p>
            </div>
            <div className="bp-reg-fields">
              <div className="bp-reg-field">
                <label className="bp-reg-label">Display Name</label>
                <IonItem className="bp-reg-input" lines="none">
                  <IonIcon icon={personOutline} slot="start" className="bp-reg-input-icon" />
                  <IonInput
                    placeholder="e.g. Thabo Mokoena"
                    value={form.displayName}
                    onIonInput={(e) => set('displayName', e.detail.value ?? '')}
                  />
                </IonItem>
                {errors.displayName && <IonNote className="bp-reg-error">{errors.displayName}</IonNote>}
              </div>
              <div className="bp-reg-field">
                <label className="bp-reg-label">Bio</label>
                <IonItem className="bp-reg-input" lines="none">
                  <IonTextarea
                    placeholder="A short description of your experience and area of focus"
                    rows={3}
                    value={form.bio}
                    onIonInput={(e) => set('bio', e.detail.value ?? '')}
                  />
                </IonItem>
              </div>
              <div className="bp-reg-row">
                <div className="bp-reg-field">
                  <label className="bp-reg-label">Phone</label>
                  <IonItem className="bp-reg-input" lines="none">
                    <IonInput
                      type="tel"
                      placeholder="+27..."
                      value={form.contactPhone}
                      onIonInput={(e) => set('contactPhone', e.detail.value ?? '')}
                    />
                  </IonItem>
                </div>
                <div className="bp-reg-field">
                  <label className="bp-reg-label">WhatsApp</label>
                  <IonItem className="bp-reg-input" lines="none">
                    <IonInput
                      type="tel"
                      placeholder="+27..."
                      value={form.whatsapp}
                      onIonInput={(e) => set('whatsapp', e.detail.value ?? '')}
                    />
                  </IonItem>
                </div>
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="bp-reg-step-body">
            <div className="bp-reg-step-head">
              <h2 className="bp-reg-step-title">Coverage area</h2>
              <p className="bp-reg-step-sub">Define the area you'll serve. You can update this later.</p>
            </div>
            <div className="bp-reg-fields">
              <div className="bp-reg-field">
                <label className="bp-reg-label">Area Name</label>
                <IonItem className="bp-reg-input" lines="none">
                  <IonIcon icon={location} slot="start" className="bp-reg-input-icon" />
                  <IonInput
                    placeholder="e.g. Sandton, Johannesburg"
                    value={form.areaLabel}
                    onIonInput={(e) => set('areaLabel', e.detail.value ?? '')}
                  />
                </IonItem>
                {errors.areaLabel && <IonNote className="bp-reg-error">{errors.areaLabel}</IonNote>}
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="bp-reg-step-body">
            <div className="bp-reg-step-head">
              <h2 className="bp-reg-step-title">Review & submit</h2>
              <p className="bp-reg-step-sub">Check your details before submitting.</p>
            </div>
            {submissionError && <IonNote color="danger" className="bp-reg-error">{submissionError}</IonNote>}
            <div className="bp-reg-review">
              <ReviewRow label="Email" value={form.email} onEdit={() => goTo(0)} />
              <ReviewRow label="Display Name" value={form.displayName} onEdit={() => goTo(1)} />
              <ReviewRow label="Bio" value={form.bio} onEdit={() => goTo(1)} />
              <ReviewRow label="Phone" value={form.contactPhone} onEdit={() => goTo(1)} />
              <ReviewRow label="WhatsApp" value={form.whatsapp} onEdit={() => goTo(1)} />
              <ReviewRow label="Coverage Area" value={form.areaLabel} onEdit={() => goTo(2)} />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <IonPage className="bp-reg-page">
      <NoindexSeo />
      <IonContent className="bp-reg-content">
        <div className="bp-reg-shell">
          {/* Brand panel */}
          <aside className="bp-reg-brand">
            <div className="bp-reg-brand-inner">
              <LogoLink className="bp-auth-logo" />
              <div className="bp-reg-brand-head">
                <div className="bp-badge">
                  <IonIcon icon={sparkles} style={{ fontSize: '0.625rem' }} />
                  Join BluePages
                </div>
                <h1 className="bp-auth-brand-title">
                  Become a{' '}
                  <span className="bp-auth-brand-accent">Service Agent</span>
                </h1>
                <p className="bp-auth-brand-sub">
                  Own your local marketplace. Onboard candidates, support employers,
                  and grow South Africa's contract workforce.
                </p>
              </div>
              <ul className="bp-auth-highlights">
                {highlights.map((h) => (
                  <li key={h.title} className="bp-auth-highlight">
                    <span className="bp-auth-highlight-icon"><IonIcon icon={h.icon} /></span>
                    <div>
                      <div className="bp-auth-highlight-title">{h.title}</div>
                      <div className="bp-auth-highlight-desc">{h.desc}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Form panel */}
          <main className="bp-reg-panel">
            <div className="bp-reg-card">
              {complete ? (
                <div className="bp-reg-step-body bp-reg-complete">
                  <div className="bp-reg-complete-badge">
                    <IonIcon icon={checkmarkCircle} />
                  </div>
                  <h2 className="bp-reg-step-title" style={{ textAlign: 'center' }}>Application submitted!</h2>
                  <p className="bp-reg-step-sub" style={{ textAlign: 'center' }}>
                    We'll review your application and get back to you within 2 business days.
                  </p>
                  <IonButton
                    className="bp-reg-submit"
                    onClick={() => history.push('/agents')}
                    style={{ maxWidth: '240px', margin: '0 auto' }}
                  >
                    Back to Agents
                  </IonButton>
                </div>
              ) : submitting ? (
                <div className="bp-reg-center bp-reg-submitting">
                  <IonSpinner />
                  <p>Submitting your application…</p>
                </div>
              ) : (
                <>
                  <div className="bp-reg-stepper">
                    {STEPS.map((s, i) => {
                      const status = i < step ? 'done' : i === step ? 'current' : 'todo';
                      return (
                        <div key={s.key} className={`bp-reg-step-dot ${status}`}>
                          <span className="bp-reg-step-num">
                            {status === 'done' ? <IonIcon icon={checkmark} /> : <IonIcon icon={s.icon} />}
                          </span>
                          <span className="bp-reg-step-name">{s.label}</span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="bp-reg-scroll">{renderStep()}</div>

                  <div className="bp-reg-footer">
                    <IonButton
                      fill="clear"
                      className="bp-reg-back"
                      onClick={back}
                      disabled={step === 0}
                    >
                      <IonIcon icon={arrowBack} slot="start" /> Back
                    </IonButton>
                    <IonButton onClick={step === STEPS.length - 1 ? submit : next} className="bp-reg-submit">
                      {step === STEPS.length - 1 ? (
                        <>Submit Application <IonIcon icon={checkmark} slot="end" /></>
                      ) : (
                        <>Next <IonIcon icon={arrowForward} slot="end" /></>
                      )}
                    </IonButton>
                  </div>
                </>
              )}
            </div>
          </main>
        </div>
        <AppFooter />
      </IonContent>
    </IonPage>
  );
}
